/**
 * Due Diligence Queue Manager
 *
 * Manages concurrent requests for building due diligence calculations
 * Prevents API overload by batching and rate-limiting requests
 */

import { getNumericValuesForBuilding } from "screens/buildings/components/operationalDashboard/utils/calculateNumericValuesForBuilding";
import { getNumericValuesForUnit } from "screens/buildings/components/operationalDashboard/utils/calculateNumericValuesForUnit";
import { DotValues, createEmptyDotValues } from "./dueDiligenceCacheService";

interface QueueTask {
  id: string;
  type: 'building' | 'unit';
  entity: any;
  categories: string[];
  session: any;
  resolve: (value: Map<string, DotValues>) => void;
  reject: (error: any) => void;
  abortController?: AbortController;
}

class DueDiligenceQueueManager {
  private queue: QueueTask[] = [];
  private processing = false;
  private maxConcurrent = 2; // Process 2 buildings/units at a time
  private currentlyProcessing = 0;

  /**
   * Add a building calculation task to the queue
   */
  async calculateBuilding(
    buildingId: string,
    building: any,
    categories: string[],
    session: any,
    abortController?: AbortController
  ): Promise<Map<string, DotValues>> {
    return new Promise((resolve, reject) => {
      const task: QueueTask = {
        id: buildingId,
        type: 'building',
        entity: building,
        categories,
        session,
        resolve,
        reject,
        abortController,
      };

      this.queue.push(task);
      this.processQueue();
    });
  }

  /**
   * Add a unit calculation task to the queue
   */
  async calculateUnit(
    unitId: string,
    unit: any,
    categories: string[],
    session: any,
    abortController?: AbortController
  ): Promise<Map<string, DotValues>> {
    return new Promise((resolve, reject) => {
      const task: QueueTask = {
        id: unitId,
        type: 'unit',
        entity: unit,
        categories,
        session,
        resolve,
        reject,
        abortController,
      };

      this.queue.push(task);
      this.processQueue();
    });
  }

  /**
   * Process the queue with concurrency control
   */
  private async processQueue() {
    if (this.processing || this.currentlyProcessing >= this.maxConcurrent) {
      return;
    }

    if (this.queue.length === 0) {
      return;
    }

    this.processing = true;
    const task = this.queue.shift();

    if (!task) {
      this.processing = false;
      return;
    }

    // Check if task was aborted
    if (task.abortController?.signal.aborted) {
      task.reject(new Error('Task aborted'));
      this.processing = false;
      this.processQueue(); // Continue with next task
      return;
    }

    this.currentlyProcessing++;

    try {
      const results = await this.executeTask(task);
      task.resolve(results);
    } catch (error) {
      task.reject(error);
    } finally {
      this.currentlyProcessing--;
      this.processing = false;
      this.processQueue(); // Process next task
    }
  }

  /**
   * Execute a single task
   */
  private async executeTask(task: QueueTask): Promise<Map<string, DotValues>> {
    const results = new Map<string, DotValues>();

    const calculateFn = task.type === 'building'
      ? getNumericValuesForBuilding
      : getNumericValuesForUnit;

    // Process categories sequentially to avoid overwhelming the API
    for (const category of task.categories) {
      // Check for abort between categories
      if (task.abortController?.signal.aborted) {
        throw new Error('Task aborted');
      }

      try {
        const dotValues = await calculateFn(task.entity, category, task.session);

        if (dotValues) {
          results.set(category, dotValues);
        } else {
          results.set(category, createEmptyDotValues());
        }
      } catch (error) {
        console.error(`[QueueManager] Error calculating ${category} for ${task.type} ${task.id}:`, error);
        // Continue with other categories even if one fails
        results.set(category, createEmptyDotValues());
      }
    }

    return results;
  }

  /**
   * Cancel all pending tasks for a specific building or unit
   */
  cancelTasks(id: string) {
    this.queue = this.queue.filter(task => {
      if (task.id === id) {
        task.reject(new Error('Task cancelled'));
        return false;
      }
      return true;
    });
  }

  /**
   * Clear all pending tasks
   */
  clearQueue() {
    this.queue.forEach(task => {
      task.reject(new Error('Queue cleared'));
    });
    this.queue = [];
  }

  /**
   * Get queue statistics
   */
  getStats() {
    return {
      queueLength: this.queue.length,
      processing: this.currentlyProcessing,
      buildingTasks: this.queue.filter(t => t.type === 'building').length,
      unitTasks: this.queue.filter(t => t.type === 'unit').length,
    };
  }

  /**
   * Update concurrency limit
   */
  setConcurrency(max: number) {
    this.maxConcurrent = Math.max(1, max);
  }
}

// Export singleton instance
export const dueDiligenceQueue = new DueDiligenceQueueManager();

/**
 * Helper function to merge multiple DotValues into one
 */
export const mergeDotValues = (dotValuesList: DotValues[]): DotValues => {
  return dotValuesList.reduce((acc, dotValues) => {
    Object.keys(dotValues).forEach(color => {
      const key = color as keyof DotValues;
      acc[key].count += dotValues[key].count;
      acc[key].labels = [...acc[key].labels, ...dotValues[key].labels];
      acc[key].sources = [...acc[key].sources, ...(dotValues[key].sources || [])];
    });
    return acc;
  }, createEmptyDotValues());
};
